// client/src/coms/voip/voiceclient_parts/roomLifecycle.js
import { Room, RoomEvent, Track } from 'livekit-client';
import {
  SPEAKING_PACKET_TOPIC,
  VOICE_ANIMATION_PACKET_TOPIC,
  VOICE_AUDIO_BITRATE_BPS,
  VOICE_AUDIO_RED,
  VOICE_STATUS_PACKET_TOPIC,
  voiceState,
} from './state';
import { fetchLocalVoiceAnimationFromBackend } from './utils';
import { logicalRoomName } from '../voiceUtils';
import { describeMicFailure, NO_SPEAK_PERMISSION_MESSAGE } from './voiceReliability';
import { ensurePlayRetryHook, playJoinChime, playLeaveChime, primeSfxCtx } from './sfx';
import { applyIncomingWatchQueuePacket, resetWatchQueueState } from '../../views/watch_together_parts/sessionQueue';
import { applyIncomingRemoteSpotifyPlaybackPacket } from '../../views/watch_together_parts/remoteSpotifyPlaybackRouter';
import { SOUNDBOARD_TOPIC, handleSoundboardPacket, leaveSoundboardRoom, setSoundboardRoom } from '../../soundboard/soundboardEngine';
import {
  applyReceivedSpeakingPacket,
  applyReceivedVoiceAnimationPacket,
  applyReceivedVoiceStatusPacket,
  broadcastLocalSpeakingState,
  broadcastLocalVoiceAnimation,
  broadcastLocalVoiceStatus,
  clearAllRemoteSpeakingStates,
  clearAllRemoteVoiceAnimations,
  clearAllRemoteVoiceStatuses,
  clearRemoteSpeakingState,
  clearRemoteVoiceAnimation,
  clearRemoteVoiceStatus,
  rebroadcastLocalVoiceState,
} from './packets';
import {
  applyRemoteAudioAudibility,
  bindParticipantPrefsOnce,
  bindProximityOnce,
  bindVoiceAnimationOnce,
  setActiveProximityState,
  startProximityPolling,
  stopProximityPolling,
} from './proximity';
import {
  applyPreferredOutputToAllEls,
  attachRemoteAudioTrack,
  bindDeviceChangeListenerOnce,
  bindMeterMonitorOnce,
  bindNoiseGateOnce,
  bindParticipantSpeakingEvents,
  bindPushToTalkOnce,
  bindSpeechEffectsOnce,
  createPreferredLocalAudioTrack,
  detachAllRemoteAudio,
  detachRemoteTrack,
  setMeterMonitorMode,
  startGatePipelineFromCaptureTrack,
  stopGatePipeline,
} from './audioRuntime';

export function createVoiceRoom({ onParticipantsChanged, onDisconnected, onReconnecting, onReconnected }) {
  const room = new Room({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      audioBitrate: VOICE_AUDIO_BITRATE_BPS,
      dtx: true,
      red: VOICE_AUDIO_RED,
    },
  });

  const notifyParticipantsChanged = () => {
    try {
      onParticipantsChanged(room);
    } catch {}
  };

  const bindAllSpeakingEvents = () => {
    try {
      if (room.localParticipant) {
        bindParticipantSpeakingEvents(room.localParticipant, notifyParticipantsChanged);
      }
    } catch {}

    try {
      room.remoteParticipants.forEach((p) => {
        bindParticipantSpeakingEvents(p, notifyParticipantsChanged);
      });
    } catch {}
  };

  room.on(RoomEvent.ParticipantConnected, (participant) => {
    try {
      primeSfxCtx();
      playJoinChime();
    } catch {}

    bindParticipantSpeakingEvents(participant, notifyParticipantsChanged);
    clearRemoteSpeakingState(participant && participant.identity);
    clearRemoteVoiceStatus(participant && participant.identity);
    clearRemoteVoiceAnimation(participant && participant.identity);
    rebroadcastLocalVoiceState();
    notifyParticipantsChanged();
  });

  room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    try {
      primeSfxCtx();
      playLeaveChime();
    } catch {}

    clearRemoteSpeakingState(participant && participant.identity);
    clearRemoteVoiceStatus(participant && participant.identity);
    clearRemoteVoiceAnimation(participant && participant.identity);

    try {
      const tracks = Array.from(participant.trackPublications.values());
      for (const pub of tracks) {
        if (pub && pub.track) detachRemoteTrack(pub.track);
      }
    } catch {}

    notifyParticipantsChanged();
  });

  room.on(RoomEvent.LocalTrackPublished, () => notifyParticipantsChanged());
  room.on(RoomEvent.LocalTrackUnpublished, () => notifyParticipantsChanged());
  room.on(RoomEvent.ActiveSpeakersChanged, () => notifyParticipantsChanged());

  room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
    attachRemoteAudioTrack(track, participant, pub);
    bindParticipantSpeakingEvents(participant, notifyParticipantsChanged);
    notifyParticipantsChanged();
  });

  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    detachRemoteTrack(track);
    notifyParticipantsChanged();
  });

  room.on(RoomEvent.TrackMuted, (pub) => {
    if (pub && pub.track) detachRemoteTrack(pub.track);
    notifyParticipantsChanged();
  });

  room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
    if (pub && pub.track) attachRemoteAudioTrack(pub.track, participant || null, pub);
    notifyParticipantsChanged();
  });

  room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
    const maybeTopic = String(topic || '').trim();

    // Moderation instructions come only from the server (participant undefined), never from peers.
    if (maybeTopic === 'vox-moderation') {
      if (participant) return;
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg && msg.__pa === 'vox_moderation_v1' && msg.type === 'move') {
          window.dispatchEvent(new CustomEvent('vox-voice-move', { detail: { to_room: msg.to_room, to_channel_id: msg.to_channel_id } }));
        }
      } catch {}
      return;
    }

    // Soundboard events are server-sent; the engine ignores copies from participants and events for
    // any room other than this one.
    if (maybeTopic === SOUNDBOARD_TOPIC) {
      handleSoundboardPacket(payload, participant, topic, logicalRoomName(room.name)).catch(() => {});
      return;
    }

    if (applyIncomingRemoteSpotifyPlaybackPacket(payload, participant, topic)) {
      return;
    }

    if (applyIncomingWatchQueuePacket(payload, participant, room, topic)) {
      return;
    }

    if (maybeTopic === VOICE_ANIMATION_PACKET_TOPIC) {
      if (applyReceivedVoiceAnimationPacket(payload, participant)) {
        notifyParticipantsChanged();
      }
      return;
    }

    if (maybeTopic === VOICE_STATUS_PACKET_TOPIC) {
      if (applyReceivedVoiceStatusPacket(payload, participant)) {
        notifyParticipantsChanged();
      }
      return;
    }

    if (maybeTopic === SPEAKING_PACKET_TOPIC) {
      if (applyReceivedSpeakingPacket(payload, participant)) {
        notifyParticipantsChanged();
      }
      return;
    }

    if (!maybeTopic) {
      if (applyReceivedSpeakingPacket(payload, participant)) {
        notifyParticipantsChanged();
        return;
      }
      if (applyReceivedVoiceStatusPacket(payload, participant)) {
        notifyParticipantsChanged();
        return;
      }
      if (applyReceivedVoiceAnimationPacket(payload, participant)) {
        notifyParticipantsChanged();
      }
    }
  });

  // LiveKit resumes or restarts the connection by itself after short network drops. Speaking/status
  // packets sent meanwhile were lost, so re-announce local state once it is back.
  room.on(RoomEvent.Reconnecting, () => {
    try {
      if (typeof onReconnecting === 'function') onReconnecting(room);
    } catch {}
  });

  room.on(RoomEvent.Reconnected, () => {
    try {
      rebroadcastLocalVoiceState();
    } catch {}
    notifyParticipantsChanged();
    try {
      if (typeof onReconnected === 'function') onReconnected(room);
    } catch {}
  });

  // The server pushes new rights into live sessions (role / override / mute changes): let the UI refresh.
  const announcePermissions = (participant) => {
    if (participant && room.localParticipant && participant !== room.localParticipant) return;
    try {
      window.dispatchEvent(new CustomEvent('vox-permissions-changed', { detail: { room: logicalRoomName(room.name) } }));
    } catch {}
  };
  room.on(RoomEvent.ParticipantAttributesChanged, (_changed, participant) => announcePermissions(participant));
  room.on(RoomEvent.ParticipantPermissionsChanged, (prev, participant) => {
    if (participant === room.localParticipant) onLocalPublishPermissionsChanged(room, prev, participant.permissions);
    announcePermissions(participant);
  });

  room.on(RoomEvent.Disconnected, (reason) => {
    // The Watch Together session lives in this room. Keeping it after leaving made a rejoining host
    // still believe it was host (and ignore the real one); on rejoin the state is fetched again.
    // A late event from an older room must not wipe the state of the room we are in now.
    if (!voiceState.activeRoom || voiceState.activeRoom === room) {
      try {
        resetWatchQueueState();
      } catch {}
      try {
        leaveSoundboardRoom(logicalRoomName(room.name));
      } catch {}
    }
    onDisconnected(room, reason);
  });

  bindAllSpeakingEvents();

  return room;
}

const MIC_WAIT_MS = 8000;

// livekit protocol TrackSource.MICROPHONE
const SOURCE_MICROPHONE = 2;
function mayPublishMicrophone(perms) {
  if (!perms) return true;
  if (perms.canPublish === false) return false;
  const sources = Array.isArray(perms.canPublishSources) ? perms.canPublishSources.map(Number) : [];
  return !sources.length || sources.includes(SOURCE_MICROPHONE);
}

// The server changed what we may publish while connected (role, channel override, server mute, timeout).
// LiveKit already stopped a microphone we may no longer use; tidy up locally and say why. When speaking
// is allowed again, capture and publish the microphone without rejoining.
function onLocalPublishPermissionsChanged(room, prev, next) {
  if (voiceState.activeRoom !== room) return;
  // Camera / screen flags the share buttons check (LiveKit TrackSource: CAMERA 1, SCREEN_SHARE 3).
  if (next) {
    const sources = Array.isArray(next.canPublishSources) ? next.canPublishSources.map(Number) : [];
    const allows = (n) => next.canPublish !== false && (!sources.length || sources.includes(n));
    voiceState.permissions = { ...(voiceState.permissions || {}), video: allows(1), screen: allows(3) };
  }
  const could = mayPublishMicrophone(prev);
  const can = mayPublishMicrophone(next);
  if (could && !can) {
    try {
      if (voiceState.activeLocalPub && voiceState.activeLocalPub.track) room.localParticipant.unpublishTrack(voiceState.activeLocalPub.track, true);
    } catch {}
    try {
      stopGatePipeline();
    } catch {}
    try {
      if (voiceState.activeCaptureTrack) voiceState.activeCaptureTrack.stop();
    } catch {}
    voiceState.activeCaptureTrack = null;
    voiceState.activeProcessedTrack = null;
    voiceState.activeLocalPub = null;
    voiceState.permissions = { ...(voiceState.permissions || {}), speak: false };
    voiceState.listenOnlyReason = 'You can no longer speak in this channel (your permissions changed), so you are listening only.';
  } else if (!could && can && !voiceState.activeLocalPub) {
    voiceState.permissions = { ...(voiceState.permissions || {}), speak: true };
    voiceState.listenOnlyReason = '';
    createPreferredLocalAudioTrack()
      .then((capture) => publishLateMicrophone(room, capture))
      .catch((e) => {
        if (voiceState.activeRoom === room) voiceState.listenOnlyReason = describeMicFailure(e);
      });
  } else {
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent('vox-voice-mic-ready'));
  } catch {}
}

// Microphone granted after we already joined listen-only.
async function publishLateMicrophone(room, capture) {
  if (!capture) return;
  if (voiceState.activeRoom !== room || voiceState.activeLocalPub) {
    try {
      capture.stop();
    } catch {}
    return;
  }
  try {
    const pipe = startGatePipelineFromCaptureTrack(capture);
    const pub = await room.localParticipant.publishTrack(pipe.processedTrack, {
      source: Track.Source.Microphone,
      audioBitrate: VOICE_AUDIO_BITRATE_BPS,
      dtx: true,
      red: VOICE_AUDIO_RED,
      forceStereo: false,
    });
    voiceState.activeCaptureTrack = capture;
    voiceState.activeProcessedTrack = pipe.processedTrack;
    voiceState.activeLocalPub = pub;
    voiceState.listenOnlyReason = '';
    broadcastLocalSpeakingState(true);
    broadcastLocalVoiceStatus(true);
    try {
      window.dispatchEvent(new CustomEvent('vox-voice-mic-ready'));
    } catch {}
  } catch (e) {
    voiceState.listenOnlyReason = 'Your microphone could not be connected, so you joined listen-only.';
    try {
      capture.stop();
    } catch {}
  }
}

export async function connectVoiceRoom({ room, url, token, roomName, permissions = null }) {
  const u = typeof url === 'string' ? url.trim() : '';
  if (!u) throw new Error('livekit_url_missing');

  if (typeof token !== 'string') {
    throw new Error('livekit_token_not_string');
  }

  const t = token.trim();
  if (!t) throw new Error('livekit_token_empty');

  await room.connect(u, t);

  try {
    window.__PA_LK_ROOM = room;
  } catch {}

  voiceState.activeRoom = room;
  // Role permissions from the token endpoint (speak / video / screen); null = unknown, allow.
  voiceState.permissions = permissions && typeof permissions === 'object' ? { ...permissions } : null;
  clearAllRemoteSpeakingStates();
  clearAllRemoteVoiceStatuses();
  clearAllRemoteVoiceAnimations();

  const resolvedRoomName = String(roomName || logicalRoomName(room.name) || '').trim();
  startProximityPolling(resolvedRoomName);
  try {
    setSoundboardRoom(resolvedRoomName);
  } catch {}

  bindDeviceChangeListenerOnce();
  bindPushToTalkOnce();
  bindNoiseGateOnce();
  bindMeterMonitorOnce();
  bindSpeechEffectsOnce();
  bindParticipantPrefsOnce();
  bindProximityOnce();
  bindVoiceAnimationOnce();

  try {
    await fetchLocalVoiceAnimationFromBackend();
  } catch {}

  // A missing/blocked microphone or no speak permission must not leave the user half-joined:
  // stay connected listen-only and report why.
  let capture = null;
  let processedMst = null;
  let pub = null;
  voiceState.listenOnlyReason = '';

  const perms = room.localParticipant && room.localParticipant.permissions;
  const mayPublish = !perms || perms.canPublish !== false;

  if (!mayPublish) {
    voiceState.listenOnlyReason = NO_SPEAK_PERMISSION_MESSAGE;
  } else {
    // An unanswered browser permission prompt would otherwise keep the join "Connecting…" forever:
    // after MIC_WAIT_MS we finish joining listen-only and publish once the user allows the microphone.
    const pendingCapture = createPreferredLocalAudioTrack();
    const timedOut = Symbol('timeout');
    try {
      const first = await Promise.race([pendingCapture, new Promise((r) => setTimeout(() => r(timedOut), MIC_WAIT_MS))]);
      if (first === timedOut) {
        voiceState.listenOnlyReason = 'Waiting for microphone access. Allow it in your browser to talk; you can listen meanwhile.';
        pendingCapture
          .then((late) => publishLateMicrophone(room, late))
          .catch((e) => {
            if (voiceState.activeRoom === room) voiceState.listenOnlyReason = describeMicFailure(e);
          });
      } else {
        capture = first;
      }
    } catch (e) {
      capture = null;
      voiceState.listenOnlyReason = describeMicFailure(e);
    }
  }

  if (capture) {
    const pipe = startGatePipelineFromCaptureTrack(capture);
    processedMst = pipe.processedTrack;

    // Tokens restrict publishing to declared sources, so the processed mic track must say it is the mic.
    const publishOpts = { source: Track.Source.Microphone, audioBitrate: VOICE_AUDIO_BITRATE_BPS, dtx: true, red: VOICE_AUDIO_RED, forceStereo: false };
    for (let attempt = 0; attempt < 2 && !pub; attempt++) {
      try {
        pub = await room.localParticipant.publishTrack(processedMst, publishOpts);
      } catch (e) {
        pub = null;
        if (attempt === 1) {
          voiceState.listenOnlyReason = /permission|not allowed|unauthori/i.test(String((e && e.message) || ''))
            ? NO_SPEAK_PERMISSION_MESSAGE
            : 'Your microphone could not be connected, so you joined listen-only.';
        } else {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
    }

    if (!pub) {
      try {
        stopGatePipeline();
      } catch {}
      try {
        capture.stop();
      } catch {}
      capture = null;
      processedMst = null;
    }
  }

  voiceState.activeCaptureTrack = capture;
  voiceState.activeProcessedTrack = processedMst;
  voiceState.activeLocalPub = pub;

  for (const p of room.remoteParticipants.values()) {
    bindParticipantSpeakingEvents(p, () => {});
    for (const pub2 of p.audioTrackPublications.values()) {
      if (pub2 && pub2.track) attachRemoteAudioTrack(pub2.track, p, pub2);
    }
  }

  try {
    await applyPreferredOutputToAllEls();
  } catch {}

  voiceState.pttHeld = false;
  broadcastLocalSpeakingState(true);
  broadcastLocalVoiceStatus(true);
  broadcastLocalVoiceAnimation(true);

  try {
    applyRemoteAudioAudibility();
  } catch {}

  ensurePlayRetryHook();
  primeSfxCtx();

  try {
    playJoinChime();
  } catch {}

  return capture;
}

export function disconnectVoiceRoom(room, track) {
  try {
    primeSfxCtx();
    playLeaveChime();
  } catch {}

  try {
    setMeterMonitorMode(false);
  } catch {}

  try {
    if (voiceState.activeCameraPub && voiceState.activeRoom && voiceState.activeRoom.localParticipant) {
      try {
        voiceState.activeRoom.localParticipant.unpublishTrack(voiceState.activeCameraPub.track || voiceState.activeCameraTrack, false);
      } catch {}
    }
  } catch {}

  try {
    if (voiceState.activeCameraTrack && typeof voiceState.activeCameraTrack.stop === 'function') voiceState.activeCameraTrack.stop();
  } catch {}

  voiceState.activeCameraTrack = null;
  voiceState.activeCameraPub = null;

  try {
    if (track) track.stop();
  } catch {}

  try {
    if (voiceState.activeCaptureTrack && voiceState.activeCaptureTrack !== track) voiceState.activeCaptureTrack.stop();
  } catch {}

  try {
    stopGatePipeline();
  } catch {}

  try {
    if (room) room.disconnect();
  } catch {}

  try {
    detachAllRemoteAudio();
  } catch {}

  if (voiceState.activeRoom === room) {
    try {
      leaveSoundboardRoom(logicalRoomName(room && room.name));
    } catch {}
    voiceState.activeRoom = null;
    voiceState.activeCaptureTrack = null;
    voiceState.activeProcessedTrack = null;
    voiceState.activeLocalPub = null;
    voiceState.activeComsRoomName = '';
    voiceState.activeServerId = '';
    stopProximityPolling();
    setActiveProximityState(null);
    clearAllRemoteSpeakingStates();
    clearAllRemoteVoiceStatuses();
    clearAllRemoteVoiceAnimations();
  }

  voiceState.pttHeld = false;
  voiceState.listenOnlyReason = '';
  voiceState.permissions = null;
}

export function getListenOnlyReason() {
  return String(voiceState.listenOnlyReason || '');
}